"use strict";

// Bunny-hop / slidehop movement mod, ported from
// Krunker_v3.1.1/mod/mods.ts (patchInputs and friends).
// ─────────────────────────────────────────────────────────
// v3.1.1 runs as an engine-scope mod bundle and intercepts the
// input frame by monkey-patching Array.prototype.push on
// CONTROLS.tmpInpts. v1.5.2 has no such bundle, so index.js calls
// bhop.patchInputs(tmpInput) directly, at the same logical point
// (after the input frame is built, before it's consumed/sent).
//
// patchInputs rewrites the jump (input[7]) and crouch (input[8])
// fields of the frame in place each tick, based on the active
// bhopMode and the held-key state tracked below. The engine then
// processes the rewritten frame in players.js procInputs exactly as
// if the player had pressed those keys.
//
// Input frame layout (matches v3.1.1 iInputs):
//   0 frame  1 delta  2 xDir  3 yDir  4 moveDir  5 shoot  6 scope
//   7 jump   8 crouch 9 reload 10 weaponScroll  11 weaponSwap
//
// NOTE on globals: v3.1.1 uses window.bhop as a boolean (Space held).
// We keep that meaning for window.bhop and expose the module API on
// window.bhopMod instead, so the two never collide.
// ─────────────────────────────────────────────────────────

// ─── Injected singletons (set at init) ──────────────────
var _Game = null;
var _getPlayer = function () { return null; };

function player() { return _getPlayer ? _getPlayer() : null; }

// ─── Bhop modes ─────────────────────────────────────────

var BhopMode = {
  DISABLED: 0,
  SLIDEHOP: 1,
  HOLD_JUMP_CROUCH: 2,
  ALWAYS_JUMP: 3,
  HOLD_JUMP: 4
};

var BHOP_MODE_ORDER = [
  BhopMode.DISABLED,
  BhopMode.SLIDEHOP,
  BhopMode.HOLD_JUMP_CROUCH,
  BhopMode.ALWAYS_JUMP,
  BhopMode.HOLD_JUMP
];

var BHOP_MODE_NAMES = {};
BHOP_MODE_NAMES[BhopMode.DISABLED] = "Disabled";
BHOP_MODE_NAMES[BhopMode.SLIDEHOP] = "Slidehop (while moving)";
BHOP_MODE_NAMES[BhopMode.HOLD_JUMP_CROUCH] = "Hold Jump/Crouch";
BHOP_MODE_NAMES[BhopMode.ALWAYS_JUMP] = "Always Jump (while moving)";
BHOP_MODE_NAMES[BhopMode.HOLD_JUMP] = "Hold Jump";

// Older builds stored HOLD_JUMP_CROUCH under a different numeric value.
var LEGACY_HOLD_JUMP_CROUCH_MODE = 5;

function isBhopMode(value) {
  return BHOP_MODE_ORDER.indexOf(value) >= 0;
}

// ─── State init (localStorage) ──────────────────────────

window.bhopMode = Number(localStorage.getItem("bhop"));
if (window.bhopMode === LEGACY_HOLD_JUMP_CROUCH_MODE) {
  window.bhopMode = BhopMode.HOLD_JUMP_CROUCH;
  try { localStorage.setItem("bhop", String(window.bhopMode)); } catch (e) {}
}
if (!Number.isFinite(window.bhopMode) || !isBhopMode(window.bhopMode)) {
  window.bhopMode = BhopMode.DISABLED;
}
window.bhopEnabled = window.bhopMode !== BhopMode.DISABLED;
// Space held (auto-jump-while-held); Shift held (slide) — updated below.
window.bhop = false;
window.slideHold = false;

// Bhop-mode cycle keybind (default B), persisted separately from the mode.
var storedBind = null;
try { storedBind = localStorage.getItem("krk_bhop_mode_bind"); } catch (e) {}
window.bhopModeKey = typeof storedBind === "string" && storedBind ? storedBind : "KeyB";

var slideDidCrouch = false;

// ─── Held-key tracking ──────────────────────────────────
//
// window.bhop mirrors Space held, window.slideHold mirrors Shift held.
// Release state must always be honored even when focus jumped to an
// input (Esc → menu), or the flags stick on and the game auto-jumps in
// the menu — hence keyup / blur / pointerlockchange all clear them.

window.addEventListener("keydown", function (e) {
  if (e.repeat || (document.activeElement && document.activeElement.tagName === "INPUT")) return;
  if (e.code === "Space") {
    window.bhop = true;
  } else if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    window.slideHold = true;
  }
});

window.addEventListener("keyup", function (e) {
  if (e.repeat) return;
  if (e.code === "Space") {
    window.bhop = false;
  } else if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
    window.slideHold = false;
  }
});

window.addEventListener("blur", function () {
  window.bhop = false;
  window.slideHold = false;
});

document.addEventListener("pointerlockchange", function () {
  if (!document.pointerLockElement) {
    window.bhop = false;
    window.slideHold = false;
  }
});

// ─── Input-frame index map ──────────────────────────────

var iInputs = {
  frame: 0,
  delta: 1,
  xDir: 2,
  yDir: 3,
  moveDir: 4,
  shoot: 5,
  scope: 6,
  jump: 7,
  crouch: 8,
  reload: 9,
  weaponScroll: 10,
  weaponSwap: 11
};

// ─── Slidehop crouch jitter ─────────────────────────────
//
// zeroSome (-0.015..0.005) randomises the falling-velocity threshold
// at which we begin crouching for the next slide, so the slidehop
// cadence doesn't look frame-perfect.

function bhopRandom(min, max, isFloat) {
  var v = Math.random() * (max - min) + min;
  return isFloat ? v : Math.floor(v);
}

function pickZeroSome() {
  return bhopRandom(-0.015, 0.005, true);
}

var bhopZeroSome = pickZeroSome();
var bhopNextZeroSome = pickZeroSome();
var bhopDidCrouch = false;
var bhopState = 0;
var rampBhopTimer = 0;
var rampBhopStart = 0;

var RAMP_BHOP_DELAY_MS = 20;
var RAMP_BHOP_DELAY_JITTER_MS = 10;

function resetRampBhopTimer() {
  rampBhopTimer = 0;
  rampBhopStart = 0;
}

// ─── Ramp detection ─────────────────────────────────────

function finiteNumber(value, fallback) {
  var n = Number(value);
  return Number.isFinite(n) ? n : (fallback || 0);
}

function collidesRampObject(lp, object) {
  var scale = finiteNumber(lp.scale);
  var height = finiteNumber(lp.height);
  var x = finiteNumber(object.x);
  var y = finiteNumber(object.y);
  var z = finiteNumber(object.z);
  var width = finiteNumber(object.width);
  var objectHeight = finiteNumber(object.height);
  var length = finiteNumber(object.length);
  return (
    finiteNumber(lp.x) - scale < x + width &&
    finiteNumber(lp.x) + scale > x - width &&
    finiteNumber(lp.z) - scale < z + length &&
    finiteNumber(lp.z) + scale > z - length &&
    finiteNumber(lp.y) <= y + objectHeight &&
    finiteNumber(lp.y) + height >= y - objectHeight
  );
}

function getProgressOnLine(sX, sZ, eX, eZ, x, z) {
  var denom = (eX - sX) * (eX - sX) + (eZ - sZ) * (eZ - sZ) || 1;
  var progress = ((x - sX) * (eX - sX) + (z - sZ) * (eZ - sZ)) / denom;
  return Math.max(0, Math.min(1, finiteNumber(progress)));
}

// v1.5.2 map manager has no getGridArrays; fall back to manager.objects
// (v3.1.1's code already does the same fallback at mods.ts:1028).
function getRampObjects(lp) {
  if (!_Game || !_Game.map || !_Game.map.manager) return [];
  var manager = _Game.map.manager;
  if (typeof manager.getGridArrays === "function") {
    var nearby = manager.getGridArrays(
      finiteNumber(lp.x),
      finiteNumber(lp.y),
      finiteNumber(lp.z)
    );
    if (Array.isArray(nearby)) return nearby;
  }
  return Array.isArray(manager.objects) ? manager.objects : [];
}

function isOnRampObject(lp) {
  var wasOnGround = !!lp.onGround && !lp.didJump;
  var objects = getRampObjects(lp);
  for (var i = 0; i < objects.length; i++) {
    var object = objects[i];
    if (!object || object.active === false || !object.ramp) continue;
    if (!collidesRampObject(lp, object)) continue;

    var objectY = finiteNumber(object.y);
    var objectHeight = finiteNumber(object.height);
    if (finiteNumber(lp.y) >= objectY + objectHeight) continue;

    var dir = finiteNumber(object.dir);
    var progress = getProgressOnLine(
      finiteNumber(object.ramp.sX),
      finiteNumber(object.ramp.sZ),
      finiteNumber(object.ramp.eX),
      finiteNumber(object.ramp.eZ),
      finiteNumber(lp.x) + finiteNumber(lp.scale) * Math.cos(dir),
      finiteNumber(lp.z) + finiteNumber(lp.scale) * Math.sin(dir)
    );
    var rampY = objectY - objectHeight + (objectHeight * 2 + 0.01) * progress;
    if (finiteNumber(lp.y) <= rampY || wasOnGround) return true;
  }
  return false;
}

function isOnRamp(lp) {
  return !!(lp && lp.onGround) && isOnRampObject(lp);
}

// On ramps, hold the jump for a short jittered window rather than
// toggling every frame — otherwise ramp bhops don't gain height.
function patchRampBhopJump(inputs) {
  var now = Date.now();
  var frame = Number(inputs[iInputs.frame]) || 0;

  if (rampBhopTimer <= now) resetRampBhopTimer();

  if (!rampBhopTimer) {
    rampBhopTimer = now + RAMP_BHOP_DELAY_MS + bhopRandom(0, RAMP_BHOP_DELAY_JITTER_MS);
    rampBhopStart = frame + bhopRandom(1, 4);
    inputs[iInputs.jump] = 0;
    return;
  }

  inputs[iInputs.jump] = frame >= rampBhopStart && now < rampBhopTimer ? 1 : 0;
}

// ─── Core input transform ───────────────────────────────

function patchInputs(inputs) {
  var lp = player();
  // Noclip owns vertical movement; don't fight it.
  if (lp && lp.noclip) {
    bhopState = 0;
    resetRampBhopTimer();
    return;
  }
  var mode = window.bhopMode | 0;
  if (mode === BhopMode.DISABLED) {
    bhopState = 0;
    resetRampBhopTimer();
    slideDidCrouch = false;
    return;
  }

  // moveDir = -1 means idle; anything else is a movement direction.
  var moving = inputs[iInputs.moveDir] !== -1;

  var jumpHeld = !!(inputs[iInputs.jump] || window.bhop);
  var crouchHeld = !!(inputs[iInputs.crouch] || window.slideHold);
  var autoBhop =
    (mode === BhopMode.SLIDEHOP || mode === BhopMode.ALWAYS_JUMP) && moving;
  var holdBhop =
    (mode === BhopMode.HOLD_JUMP_CROUCH || mode === BhopMode.HOLD_JUMP) && jumpHeld;
  var wantBhop = autoBhop || holdBhop;

  if (wantBhop) {
    if (isOnRamp(lp)) {
      bhopState = 0;
      patchRampBhopJump(inputs);
    } else {
      resetRampBhopTimer();
      // Toggle jump every other frame so the engine sees a fresh
      // press each landing (jump edge-triggers on !didJump && input[7]).
      bhopState ^= 1;
      inputs[iInputs.jump] = bhopState;
    }
  } else {
    bhopState = 0;
    resetRampBhopTimer();
  }

  // Slidehop crouch logic: SLIDEHOP is automatic; HOLD_JUMP_CROUCH only
  // runs while crouch is held. HOLD_JUMP deliberately stays jump-only.
  var wantSlidehop =
    mode === BhopMode.SLIDEHOP ||
    (mode === BhopMode.HOLD_JUMP_CROUCH && crouchHeld);
  if (wantSlidehop) {
    var onGround = !!(lp && lp.onGround);
    if (onGround && !inputs[iInputs.jump] && crouchHeld) {
      inputs[iInputs.crouch] = 1;
      bhopDidCrouch = true;
    } else {
      if (!bhopDidCrouch) bhopZeroSome = bhopNextZeroSome;
      var vy = (lp && lp.yVel) || 0;
      var willCrouch = !onGround && vy < bhopZeroSome;
      inputs[iInputs.crouch] = willCrouch ? 1 : 0;
      if (!bhopDidCrouch && willCrouch) bhopNextZeroSome = pickZeroSome();
      bhopDidCrouch = willCrouch;
    }
    slideDidCrouch = bhopDidCrouch;
  } else {
    bhopDidCrouch = false;
    slideDidCrouch = false;
  }
}

// ─── Mode control ───────────────────────────────────────

// Parse a /bhop argument to a mode number (null = invalid).
// Empty toggles between DISABLED and HOLD_JUMP_CROUCH.
function resolveBhopMode(arg, currentMode) {
  if (!arg) return currentMode > 0 ? BhopMode.DISABLED : BhopMode.HOLD_JUMP_CROUCH;
  var raw = String(arg).trim().toLowerCase();
  if (["off", "0", "disable", "disabled"].indexOf(raw) >= 0) return BhopMode.DISABLED;
  if (["slide", "slidehop", "1"].indexOf(raw) >= 0) return BhopMode.SLIDEHOP;
  if (["holdjumpcrouch", "hold", "jumpcrouch", "2"].indexOf(raw) >= 0) return BhopMode.HOLD_JUMP_CROUCH;
  if (["always", "alwaysjump", "auto", "3"].indexOf(raw) >= 0) return BhopMode.ALWAYS_JUMP;
  if (["holdjump", "on", "enable", "enabled", "4"].indexOf(raw) >= 0) return BhopMode.HOLD_JUMP;
  return null;
}

function setBhopMode(mode) {
  if (!isBhopMode(mode)) return;
  window.bhopMode = mode;
  window.bhopEnabled = mode !== BhopMode.DISABLED;
  try { localStorage.setItem("bhop", String(mode)); } catch (e) {}
}

function cycleBhopMode() {
  var current = isBhopMode(window.bhopMode) ? window.bhopMode : BhopMode.DISABLED;
  var next = BHOP_MODE_ORDER[(BHOP_MODE_ORDER.indexOf(current) + 1) % BHOP_MODE_ORDER.length];
  setBhopMode(next);
  return next;
}

// ─── B-key mode cycle ───────────────────────────────────
//
// Pressing the bind (default B) cycles through the bhop modes and shows
// a toast. Gated on not-in-input and console-closed, matching v3.1.1's
// `!input && !devConsole.visible && matchesBhopModeBind(e)` guard.

function matchesBhopModeBind(e) {
  return !!window.bhopModeKey && e.code === window.bhopModeKey;
}

window.addEventListener("keydown", function (e) {
  if (e.repeat) return;
  if (document.activeElement && document.activeElement.tagName === "INPUT") return;
  if (window.devConsole && window.devConsole.visible) return;
  if (!matchesBhopModeBind(e)) return;
  var next = cycleBhopMode();
  var msg = "Bhop - " + (BHOP_MODE_NAMES[next] || "Disabled");
  if (typeof window.notify === "function") window.notify(msg);
  console.log(msg);
});

// ─── Exports ────────────────────────────────────────────

var api = {
  patchInputs: patchInputs,
  setMode: setBhopMode,
  cycleMode: cycleBhopMode,
  resolveMode: resolveBhopMode,
  modeName: function (m) { return BHOP_MODE_NAMES[m] || "Disabled"; },
  Mode: BhopMode
};

module.exports = {
  init: function (deps) {
    _Game = deps.Game;
    _getPlayer = deps.getPlayer || function () { return null; };
    // API object on window.bhopMod — NOT window.bhop (that's the boolean
    // Space-held flag the keydown/keyup handlers above maintain).
    window.bhopMod = api;
  },
  patchInputs: patchInputs,
  setMode: setBhopMode,
  cycleMode: cycleBhopMode,
  resolveMode: resolveBhopMode,
  modeName: function (m) { return BHOP_MODE_NAMES[m] || "Disabled"; },
  Mode: BhopMode
};
